import { useCallback, useMemo, useState } from 'react'

import { getPropertyRateUpgradeCandidate } from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import PropertyOfferPageLoyaltyStayUpgradeCallOutBanner from './PropertyOfferPageLoyaltyStayUpgradeCallOutBanner'
import usePropertyRateStayUpgrade from './usePropertyRateStayUpgrade'
import PropertyRateOption from './PropertyRateOption'
import PropertyRefundabilitySelector from './PropertyRefundabilitySelector'

interface Props {
  lpcOffer?: App.HotelOffer
  rates: Array<App.EnrichedPropertyRate>
  defaultRateId: string
  /** The property room's real id, distinct from `roomListId` (an analytics-only grouping id). */
  roomId: string
  roomListId: string
  roomPosition: number
  rateListId: string
  propertyTimezone: string
}

/**
 * Merged variant that combines property rates with radio button selection.
 *
 * New UI structure: Radio selector for rates (by rate ID) injected into PropertyRateOption where the cancellation policy would normally go
 */
function PropertyRateOptionMerged(props: Props) {
  const {
    lpcOffer,
    rates,
    defaultRateId,
    roomId,
    roomListId,
    roomPosition,
    rateListId,
    propertyTimezone,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  // Keep refundability selection local to this card. Room-level state only tracks the last rate for
  // the Trip Planner snapshot.
  const [cardSelectedRateId, setCardSelectedRateId] = useState(defaultRateId)

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === cardSelectedRateId) ??
      rates.find(rate => rate.rateId === defaultRateId) ??
      rates[0]
  }, [rates, cardSelectedRateId, defaultRateId])
  const selectedRateId = activeRate?.rateId

  const hasChoice = rates.length > 1

  const upgradeCandidate = useMemo(() => getPropertyRateUpgradeCandidate(rates), [rates])
  const { appliedVariant, lockedRateIds, removeUpgrade } = usePropertyRateStayUpgrade(upgradeCandidate, roomId, selectedRateId)

  const setSelectedRateId = useCallback((rateId: string) => {
    if (appliedVariant && appliedVariant.rate.rateId !== rateId) {
      removeUpgrade()
    }
    setCardSelectedRateId(rateId)
    offerPageDispatch({ type: OfferPageActions.setViewedRoomRateId, roomId, rateId })
  }, [offerPageDispatch, roomId, appliedVariant, removeUpgrade])

  if (!activeRate) {
    return null
  }

  const cancellationPolicyContentOverride =
    hasChoice ?
        <PropertyRefundabilitySelector
          rates={rates}
          selectedRateId={selectedRateId}
          onChange={setSelectedRateId}
          propertyTimezone={propertyTimezone}
          lockedRateIds={lockedRateIds}
          onRemoveUpgrade={removeUpgrade}
        /> :
      undefined

  return <PropertyRateOption
    lpcOffer={lpcOffer}
    rate={activeRate}
    roomListId={roomListId}
    roomPosition={roomPosition}
    rateListId={rateListId}
    ratePosition={activeRate.defaultOrder + 1}
    cancellationPolicyContentOverride={cancellationPolicyContentOverride}
    loyaltyUpgradeBanner={upgradeCandidate && <PropertyOfferPageLoyaltyStayUpgradeCallOutBanner
      candidate={upgradeCandidate}
      roomId={roomId}
      selectedRateId={selectedRateId}
      onApplyRateSelected={setCardSelectedRateId}
      propertyTimeZone={propertyTimezone}
    />}
    packageUpgradeTargetRate={upgradeCandidate?.targetRate}
  />
}

export default PropertyRateOptionMerged
